import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { iNavigation } from '../../../common/interfaces/index';

const SIDEBAR_OPEN_KEY = 'sidebar_is_open';

export const useSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_OPEN_KEY) !== 'false',
  );
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);

  const isVisuallyExpanded = isOpenMenu || isHovering;

  const handleToggleMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    setIsOpenMenu(prev => {
      const next = !prev;

      if (next) {
        setIsHovering(false);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      }

      localStorage.setItem(SIDEBAR_OPEN_KEY, String(next));
      return next;
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isOpenMenu) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setIsHovering(true);
    }
  }, [isOpenMenu]);

  const handleMouseLeave = useCallback(() => {
    if (!isOpenMenu) {
      hoverTimeoutRef.current = window.setTimeout(() => {
        setIsHovering(false);
      }, 100);
    }
  }, [isOpenMenu]);

  const handleItemClick = useCallback(
    (item: iNavigation) => {
      const itemPath = item.path || item.route;
      if (itemPath) {
        navigate(itemPath);
        localStorage.setItem(
          'activeMenu',
          JSON.stringify({
            route: itemPath,
            title: item.title,
            type: item.type,
          }),
        );
      }
    },
    [navigate],
  );

  const isItemActive = useCallback(
    (item: iNavigation): boolean => {
      if (typeof item.isActive === 'boolean') {
        return item.isActive;
      }

      const itemPath = item.path || item.route;
      if (!itemPath) {
        return false;
      }

      return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
    },
    [pathname],
  );

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpenMenu) {
      setIsHovering(false);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  }, [isOpenMenu]);

  return {
    isOpenMenu,
    isHovering,
    isVisuallyExpanded,
    handleToggleMenu,
    handleMouseEnter,
    handleMouseLeave,
    handleItemClick,
    isItemActive,
  };
};
